// Button — the spec's CTA variants as one component. Real <button> elements
// (the design used div+onClick throughout), with the press-scale classes and
// a pending state for the server-confirm write pipeline.

import type { CSSProperties, ReactNode } from 'react';

/**
 * Which surface a button wears.
 *
 * The rule the screens follow, so that the same action is never two colours and
 * two different actions never share one:
 *
 * `primary` (`--accent`) is the go-button — the tap a screen exists to collect
 * where the cost of a wrong tap is a trip back. "Open pod", "Record", "Confirm
 * complete", "Next pod →". It is what the prototype draws for every CTA, on the
 * phone and the board alike, and it is the one colour an operator can change in
 * settings, so it is the app's own voice.
 *
 * `ink` is louder, and it is spent on one thing: the two taps in the job footer
 * that move the pod and the operator's clock in Odoo — "Start work" and
 * "Complete & advance". Opening a pod is a look and starting it is a shift; they
 * were the same colour, and this is the difference drawn rather than explained.
 * Only ever one of the two is on screen, so the footer button changing colour is
 * also the moment the job actually began. Ink is the highest-contrast surface
 * there is in either theme, which is the right weight for the tap you cannot
 * untake by pressing Back.
 *
 * That leaves `ink` used elsewhere only where the same "this is not a
 * suggestion" weight applies — the scan row while it sweeps, and the button that
 * closes an overlay and hands the screen back to work.
 */
export type ButtonVariant = 'primary' | 'ink' | 'surface' | 'soft' | 'danger' | 'dangerGhost' | 'pos';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const HEIGHT: Record<ButtonSize, number> = { sm: 36, md: 44, lg: 52, xl: 58 };
const RADIUS: Record<ButtonSize, number> = { sm: 11, md: 13, lg: 15, xl: 16 };
// The reset in tokens.css zeroes button padding, so without this a label runs
// edge to edge, and a two-line one ("2 of 5 checks done" in a third of a row)
// touches the top and bottom too.
const PAD_X: Record<ButtonSize, number> = { sm: 12, md: 16, lg: 18, xl: 20 };
const PAD_Y: Record<ButtonSize, number> = { sm: 6, md: 8, lg: 10, xl: 10 };
const FONT: Record<ButtonSize, string> = {
  sm: "600 12.5px 'Space Grotesk',sans-serif",
  md: "600 14px 'Space Grotesk',sans-serif",
  lg: "700 15px 'Space Grotesk',sans-serif",
  xl: "700 16px 'Space Grotesk',sans-serif",
};

function surfaceFor(variant: ButtonVariant): CSSProperties {
  switch (variant) {
    case 'primary':
      return { background: 'var(--accent)', color: 'var(--accentInk)', boxShadow: '0 8px 20px var(--soft)' };
    case 'ink':
      return { background: 'var(--ink)', color: 'var(--bg)' };
    case 'surface':
      return { background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' };
    case 'soft':
      return { background: 'var(--surface2)', color: 'var(--ink)' };
    case 'danger':
      return { background: 'var(--danger)', color: '#fff' };
    case 'dangerGhost':
      return { background: 'var(--surface)', color: 'var(--danger)', border: '1px solid var(--line)' };
    case 'pos':
      return { background: 'var(--pos)', color: '#fff' };
  }
}

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  /** Awaiting server confirmation — blocks re-entry and announces busy. */
  pending?: boolean;
  icon?: ReactNode;
  full?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  'aria-label'?: string;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'lg',
  disabled = false,
  pending = false,
  icon,
  full = true,
  onClick,
  type = 'button',
  style,
  children,
  ...rest
}: ButtonProps) {
  const inert = disabled || pending;
  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    // A floor, not a fixed height: a narrow button whose label wraps (long
    // Tagalog strings, three buttons sharing a phone row) grows instead of
    // spilling its text over the border.
    minHeight: HEIGHT[size],
    padding: `${PAD_Y[size]}px ${PAD_X[size]}px`,
    borderRadius: RADIUS[size],
    font: FONT[size],
    lineHeight: 1.2,
    // The reset sets text-align: inherit, which left-aligns a wrapped label
    // inside an otherwise centred button.
    textAlign: 'center',
    width: full ? '100%' : undefined,
    ...surfaceFor(variant),
  };
  const disabledStyle: CSSProperties = inert
    ? {
        background: 'var(--surface2)',
        color: 'var(--sub)',
        border: 'none',
        boxShadow: 'none',
        cursor: 'default',
        ...(pending ? { animation: 'ipPulse 1.2s infinite' } : {}),
      }
    : {};

  return (
    <button
      type={type}
      className={inert ? undefined : 'press-soft'}
      disabled={inert}
      aria-busy={pending || undefined}
      onClick={inert ? undefined : onClick}
      style={{ ...base, ...disabledStyle, ...style }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

/** Square icon button. */
export function IconButton({
  // 44px is the smallest reliable target for a gloved hand; the visual box can
  // still look smaller via padding if a design calls for it.
  size = 44,
  onClick,
  label,
  children,
  style,
}: {
  size?: number;
  onClick?: () => void;
  label: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      className="press"
      aria-label={label}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.3),
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        color: 'var(--ink)',
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
        ...style,
      }}
    >
      {children}
    </button>
  );
}
